import React, { useEffect, useState } from "react";
import {
    adminListCareers,
    adminCreateCareer,
    adminUpdateCareer,
    adminDeleteCareer,
} from "@/lib/api";

const EMPLOYMENT_TYPES = [
    "Full-time",
    "Part-time",
    "Contract",
    "Freelance",
    "Internship",
];

const empty = {
    title: "",
    department: "",
    location: "Remote",
    employment_type: EMPLOYMENT_TYPES[0],
    description: "",
    apply_email: "careers@depmont.com",
    published: true,
    order: 0,
};

export default function CareersAdminPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");

    const formatError = (e) => {
        const d = e?.response?.data?.detail;
        if (Array.isArray(d)) {
            return d
                .map((x) => {
                    const field = Array.isArray(x?.loc)
                        ? x.loc.slice(1).join(".") || x.loc.join(".")
                        : "field";
                    return `${field}: ${x?.msg || "invalid"}`;
                })
                .join(" · ");
        }
        if (typeof d === "string") return d;
        if (d && typeof d === "object") return JSON.stringify(d);
        return e?.message || "Unknown error";
    };

    const load = async () => {
        setLoading(true);
        try {
            setItems(await adminListCareers());
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const openNew = () => {
        setSaveError("");
        setEditing({ new: true, ...empty });
    };
    const openEdit = (row) => {
        setSaveError("");
        setEditing({ ...row });
    };
    const close = () => {
        setSaveError("");
        setEditing(null);
    };

    const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

    const save = async () => {
        setSaveError("");
        if (!editing.title?.trim()) {
            setSaveError("Title is required.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: editing.title,
                department: editing.department || "",
                location: editing.location || "Remote",
                employment_type: editing.employment_type || "Full-time",
                description: editing.description || "",
                apply_email: editing.apply_email || "careers@depmont.com",
                published: editing.published,
                order: Number(editing.order) || 0,
            };
            if (editing.new) {
                await adminCreateCareer(payload);
            } else {
                await adminUpdateCareer(editing.id, payload);
            }
            close();
            await load();
        } catch (e) {
            setSaveError("Save failed — " + formatError(e));
        } finally {
            setSaving(false);
        }
    };

    const del = async (row) => {
        if (!window.confirm(`Delete "${row.title}"? This cannot be undone.`))
            return;
        await adminDeleteCareer(row.id);
        await load();
    };

    return (
        <section data-testid="admin-careers">
            <header className="flex items-end justify-between border-b border-[#E6E2D6] pb-6 mb-8">
                <div>
                    <p className="eyebrow text-[#1B1B1B]/60 mb-2">
                        § Careers — 02
                    </p>
                    <h1 className="font-serif-editorial text-4xl md:text-5xl leading-[1.02] tracking-[-0.02em]">
                        Open Positions
                    </h1>
                </div>
                <button
                    type="button"
                    onClick={openNew}
                    data-testid="career-new"
                    className="inline-flex items-center gap-3 px-7 py-3 bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[12px] tracking-[0.2em] uppercase hover:bg-[#A8844F] transition-colors duration-500"
                >
                    + New Position
                </button>
            </header>

            <p className="font-sans-tight text-sm text-[#1B1B1B]/70 mb-8 max-w-2xl font-light">
                Published positions appear at{" "}
                <span className="text-[#A8844F]">depmont.com/careers</span> — a
                public URL you can share directly when hiring. Unpublished
                positions stay hidden from the site.
            </p>

            {loading ? (
                <p className="eyebrow text-[#1B1B1B]/50">Loading…</p>
            ) : items.length === 0 ? (
                <p className="font-serif-editorial italic text-2xl text-[#1B1B1B]/70">
                    No positions yet. The careers page will show an
                    "applications always welcome" note until you add one.
                </p>
            ) : (
                <div className="border-t border-[#E6E2D6]">
                    {items.map((row) => (
                        <div
                            key={row.id}
                            data-testid={`career-row-${row.id}`}
                            className="grid grid-cols-12 gap-4 items-center py-5 border-b border-[#E6E2D6]"
                        >
                            <div className="col-span-6">
                                <p className="eyebrow text-[#1B1B1B]/60 mb-1">
                                    {row.department || "—"} · {row.location} ·{" "}
                                    {row.employment_type}
                                </p>
                                <h3 className="font-serif-editorial text-2xl leading-[1.1]">
                                    {row.title}
                                </h3>
                            </div>
                            <div className="col-span-3">
                                <p className="eyebrow text-[#1B1B1B]/60">
                                    {row.published ? "Published" : "Hidden"}
                                </p>
                                <p className="font-sans-tight text-xs text-[#1B1B1B]/60">
                                    Order · {row.order ?? 0}
                                </p>
                            </div>
                            <div className="col-span-3 flex gap-6 justify-end">
                                <button
                                    type="button"
                                    onClick={() => openEdit(row)}
                                    data-testid={`career-edit-${row.id}`}
                                    className="eyebrow link-underline"
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    onClick={() => del(row)}
                                    data-testid={`career-delete-${row.id}`}
                                    className="eyebrow link-underline text-red-800"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Editor drawer */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-[#1B1B1B]/40 flex justify-end">
                    <div className="w-full max-w-2xl h-full overflow-y-auto bg-[#F6F3EC] p-8 md:p-12">
                        <div className="flex items-center justify-between mb-8">
                            <p className="eyebrow text-[#1B1B1B]/60">
                                {editing.new ? "New Position" : "Edit Position"}
                            </p>
                            <button
                                type="button"
                                onClick={close}
                                className="eyebrow link-underline"
                            >
                                Close ✕
                            </button>
                        </div>

                        <div className="space-y-6">
                            <Field label="Title">
                                <input
                                    className="editorial-input"
                                    value={editing.title}
                                    onChange={(e) => setField("title", e.target.value)}
                                    placeholder="Middleweight Designer"
                                    data-testid="career-title"
                                />
                            </Field>

                            <div className="grid grid-cols-2 gap-6">
                                <Field label="Department">
                                    <input
                                        className="editorial-input"
                                        value={editing.department}
                                        onChange={(e) =>
                                            setField("department", e.target.value)
                                        }
                                        placeholder="Design"
                                    />
                                </Field>
                                <Field label="Location">
                                    <input
                                        className="editorial-input"
                                        value={editing.location}
                                        onChange={(e) =>
                                            setField("location", e.target.value)
                                        }
                                        placeholder="Remote"
                                    />
                                </Field>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <Field label="Employment Type">
                                    <select
                                        className="editorial-input"
                                        value={editing.employment_type}
                                        onChange={(e) =>
                                            setField("employment_type", e.target.value)
                                        }
                                    >
                                        {EMPLOYMENT_TYPES.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field
                                    label="Apply Email"
                                    hint="Where the Apply button sends applicants."
                                >
                                    <input
                                        className="editorial-input"
                                        value={editing.apply_email}
                                        onChange={(e) =>
                                            setField("apply_email", e.target.value)
                                        }
                                        placeholder="careers@depmont.com"
                                    />
                                </Field>
                            </div>

                            <Field
                                label="Description"
                                hint="What the role involves and who it suits. Line breaks are preserved on the public page."
                            >
                                <textarea
                                    rows={8}
                                    className="editorial-input resize-none"
                                    value={editing.description}
                                    onChange={(e) =>
                                        setField("description", e.target.value)
                                    }
                                    data-testid="career-description"
                                />
                            </Field>

                            <div className="grid grid-cols-2 gap-6">
                                <Field label="Published">
                                    <select
                                        className="editorial-input"
                                        value={editing.published ? "yes" : "no"}
                                        onChange={(e) =>
                                            setField("published", e.target.value === "yes")
                                        }
                                    >
                                        <option value="yes">Yes — live on site</option>
                                        <option value="no">No — hidden</option>
                                    </select>
                                </Field>
                                <Field label="Order">
                                    <input
                                        type="number"
                                        className="editorial-input"
                                        value={editing.order ?? 0}
                                        onChange={(e) => setField("order", e.target.value)}
                                    />
                                </Field>
                            </div>
                        </div>

                        <div className="mt-10 pt-6 border-t border-[#E6E2D6] flex flex-col gap-4">
                            {saveError && (
                                <div
                                    data-testid="career-save-error"
                                    className="border border-red-800/40 bg-red-800/5 text-red-800 font-sans-tight text-sm p-4"
                                >
                                    {saveError}
                                </div>
                            )}
                            <div className="flex justify-end gap-4">
                                <button
                                    type="button"
                                    onClick={close}
                                    className="eyebrow link-underline"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={save}
                                    disabled={saving}
                                    data-testid="career-save"
                                    className="inline-flex items-center gap-3 px-7 py-3 bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[12px] tracking-[0.2em] uppercase hover:bg-[#A8844F] transition-colors duration-500 disabled:opacity-60"
                                >
                                    {saving ? "Saving…" : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

const Field = ({ label, hint, children }) => (
    <div>
        <label className="eyebrow text-[#1B1B1B]/60">{label}</label>
        {children}
        {hint && (
            <p className="mt-2 font-sans-tight text-xs text-[#1B1B1B]/55 leading-[1.5] max-w-md font-light">
                {hint}
            </p>
        )}
    </div>
);
